import { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Copy, CreditCard, Download, Headphones, KeyRound, Loader2, Package, Printer } from "lucide-react";
import { api } from "@/lib/api";
import { formatPrice, formatDateTime, PAYMENT_LABELS } from "@/lib/format";
import { useStore } from "@/context/StoreContext";
import { useSeo } from "@/lib/seo";
import RichText from "@/components/RichText";

export default function OrderConfirmation() {
  const { orderNumber } = useParams();
  const location = useLocation();
  const { currency } = useStore();
  const [order, setOrder] = useState(location.state?.order || null);
  const [loading, setLoading] = useState(!location.state?.order);
  const [email, setEmail] = useState(location.state?.email || "");

  useSeo({ title: `Commande ${orderNumber} — ISMO-TECH`, noIndex: true });

  useEffect(() => {
    if (order) return;
    setLoading(true);
    api
      .get(`/orders/${orderNumber}`, { params: email ? { email } : {} })
      .then(({ data }) => setOrder(data))
      .catch(() => setOrder(null))
      .finally(() => setLoading(false));
  }, [orderNumber, order, email]);

  const copy = (value) => {
    navigator.clipboard?.writeText(value);
    toast.success("Clé copiée dans le presse-papier");
  };

  if (loading) {
    return (
      <div className="container-it flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-brand" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="container-it py-20 text-center">
        <h1 className="font-display text-2xl font-bold text-ink">Commande introuvable</h1>
        <p className="mt-2 text-sm text-slate-500">
          Saisissez l'email utilisé lors de la commande pour retrouver vos licences.
        </p>
        <div className="mx-auto mt-6 flex max-w-md gap-2">
          <input
            data-testid="lookup-email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="votre@email.com"
            className="h-12 flex-1 rounded-xl border border-line px-4 text-sm"
          />
          <button
            data-testid="lookup-submit"
            onClick={() => setLoading(true)}
            className="rounded-xl bg-brand px-5 text-sm font-bold text-white"
          >
            Rechercher
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container-it py-8 lg:py-14">
      <div className="mx-auto max-w-3xl">
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-center sm:p-8">
          <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500 text-white">
            <CheckCircle2 className="h-9 w-9" />
          </span>
          <h1 className="mt-5 font-display text-2xl font-extrabold text-ink sm:text-3xl" data-testid="order-success-title">
            {order.payment_status === "paid" ? "Merci, votre commande est confirmée !" : "Votre commande est enregistrée !"}
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            Commande <span className="font-bold" data-testid="order-number">{order.order_number}</span> ·{" "}
            {formatDateTime(order.created_at)}
          </p>
          {order.requires_activation && order.payment_status === "paid" && (
            <p className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-xs font-semibold text-amber-800" data-testid="order-processing-note">
              ⏳ Votre commande est en cours de traitement — notre équipe prépare votre livraison manuellement.
              Suivez l'avancement dans « Mes commandes ».
            </p>
          )}
          {order.payment_status === "paid" ? (
            <p className="mt-3 inline-block rounded-full bg-white/70 px-4 py-1.5 text-xs font-semibold text-emerald-700">
              🟢 Paiement confirmé ✓
            </p>
          ) : order.status === "payment_review" ? (
            <p className="mt-3 inline-block rounded-full border border-amber-200 bg-white/70 px-4 py-1.5 text-xs font-semibold text-amber-700" data-testid="confirmation-payment-review">
              🟠 Paiement à vérifier — notre équipe contrôle votre transaction
            </p>
          ) : (
            <div className="mt-4" data-testid="confirmation-payment-cta">
              <p className="text-xs font-semibold text-amber-700">
                {order.status === "payment_rejected"
                  ? "🔴 Votre paiement a été rejeté — vous pouvez refaire une démarche de paiement."
                  : "💳 Votre commande est en attente de paiement."}
              </p>
              <Link
                to={`/paiement/${order.order_number}`}
                state={{ order, email }}
                data-testid="confirmation-pay-btn"
                className="btn-glow mt-3 inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-bold text-white"
              >
                <CreditCard className="h-4 w-4" /> Effectuer le paiement — {formatPrice(order.total, "XOF")}
              </Link>
            </div>
          )}
        </div>

        <section className="mt-6 rounded-2xl border border-line bg-white p-6">
          <p className="flex items-center gap-2 font-display text-lg font-bold text-ink">
            <KeyRound className="h-5 w-5 text-brand" /> Suivi de votre livraison
          </p>

          <div className="mt-4 space-y-3" data-testid="delivered-licenses">
            {(order.licenses || []).map((license, i) => (
              <div key={i} className="rounded-xl border border-line bg-surface p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-bold text-ink">{license.product_name}</p>
                  {!license.key_value && (
                    <span
                      data-testid={`license-status-${i}`}
                      className={`rounded-full border px-3 py-1 text-[11px] font-bold ${
                        license.status === "delivered"
                          ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                          : "border-amber-200 bg-amber-50 text-amber-700"
                      }`}
                    >
                      {license.status === "delivered" ? "Activé ✓" : "En cours de traitement"}
                    </span>
                  )}
                </div>
                {license.key_value ? (
                  <div className="mt-3 flex flex-wrap items-center gap-3">
                    <code
                      data-testid={`license-key-${i}`}
                      className="flex-1 rounded-lg border border-brand/20 bg-white px-4 py-3 font-mono text-sm font-bold tracking-wider text-brand"
                    >
                      {license.key_value}
                    </code>
                    <button
                      data-testid={`copy-license-${i}`}
                      onClick={() => copy(license.key_value)}
                      className="flex items-center gap-2 rounded-lg bg-ink px-4 py-3 text-xs font-bold text-white transition-colors duration-200 hover:bg-brand"
                    >
                      <Copy className="h-3.5 w-3.5" /> Copier
                    </button>
                  </div>
                ) : license.status === "delivered" && license.admin_message ? (
                  <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                    <p className="whitespace-pre-line text-xs leading-relaxed text-emerald-800">
                      <RichText text={license.admin_message} />
                    </p>
                    {license.credentials && (
                      <code className="mt-2 block rounded-md bg-white px-3 py-2 font-mono text-xs font-bold text-emerald-700">
                        {license.credentials}
                      </code>
                    )}
                  </div>
                ) : (
                  <p className="mt-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                    Notre équipe prépare votre livraison — vous la retrouverez dans « Mes commandes »
                    {license.delivery_time ? ` (délai estimé : ${license.delivery_time})` : ""}. <RichText text={license.instructions} />
                  </p>
                )}
                {(license.fields || []).length > 0 && (
                  <div className="mt-3 rounded-lg border border-line bg-white p-3" data-testid={`license-fields-${i}`}>
                    <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Informations fournies</p>
                    <dl className="mt-1.5 space-y-1">
                      {license.fields.map((field) => (
                        <div key={field.key} className="flex gap-2 text-xs">
                          <dt className="font-semibold text-slate-500">{field.label} :</dt>
                          <dd className="font-bold text-ink">{field.value}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                )}
                {license.key_value && license.instructions && (
                  <p className="mt-2 text-[11px] leading-snug text-slate-500">
                    <RichText text={license.instructions} />
                  </p>
                )}
              </div>
            ))}
          </div>
        </section>

        <section className="mt-6 rounded-2xl border border-line bg-white p-6">
          <p className="flex items-center gap-2 font-display text-lg font-bold text-ink">
            <Package className="h-5 w-5 text-brand" /> Détail de la commande
          </p>
          <ul className="mt-4 divide-y divide-line">
            {(order.items || []).map((item) => (
              <li key={item.product_id} className="flex items-center gap-3 py-3">
                <img
                  src={item.image_url}
                  alt={item.name}
                  className="h-12 w-12 rounded-lg border border-line bg-slate-50 object-contain p-1"
                />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold text-ink">{item.name}</span>
                  <span className="text-xs text-slate-500">Qté {item.quantity} · {item.license_type}</span>
                </span>
                <span className="text-sm font-bold text-ink">{formatPrice(item.line_total, currency)}</span>
              </li>
            ))}
          </ul>

          <dl className="mt-4 space-y-2 border-t border-line pt-4 text-sm">
            <div className="flex justify-between">
              <dt className="text-slate-500">Sous-total</dt>
              <dd className="font-semibold">{formatPrice(order.subtotal, currency)}</dd>
            </div>
            {order.discount > 0 && (
              <div className="flex justify-between">
                <dt className="text-slate-500">Réduction ({order.promo_code})</dt>
                <dd className="font-semibold text-emerald-600">−{formatPrice(order.discount, currency)}</dd>
              </div>
            )}
            {order.fees > 0 && (
              <div className="flex justify-between">
                <dt className="text-slate-500">Frais</dt>
                <dd className="font-semibold">{formatPrice(order.fees, currency)}</dd>
              </div>
            )}
            <div className="flex items-baseline justify-between border-t border-line pt-2">
              <dt className="font-bold text-ink">Total payé</dt>
              <dd className="font-display text-xl font-black text-brand" data-testid="order-total">
                {formatPrice(order.total, currency)}
              </dd>
            </div>
          </dl>
        </section>

        <div className="mt-6 grid gap-3 sm:grid-cols-3">
          <Link
            to="/compte/commandes"
            data-testid="go-to-licenses"
            className="flex items-center justify-center gap-2 rounded-full bg-brand px-5 py-3.5 text-sm font-bold text-white"
          >
            <Download className="h-4 w-4" /> Suivre ma commande
          </Link>
          <button
            onClick={() => window.print()}
            data-testid="print-invoice"
            className="flex items-center justify-center gap-2 rounded-full border-2 border-ink px-5 py-3.5 text-sm font-bold text-ink transition-colors duration-200 hover:border-brand hover:text-brand"
          >
            <Printer className="h-4 w-4" /> Imprimer la facture
          </button>
          <Link
            to="/support#ticket"
            className="flex items-center justify-center gap-2 rounded-full border border-line px-5 py-3.5 text-sm font-bold text-ink transition-colors duration-200 hover:border-brand hover:text-brand"
          >
            <Headphones className="h-4 w-4" /> Besoin d'aide ?
          </Link>
        </div>
      </div>
    </div>
  );
}
